import { useParams } from 'react-router-dom';

import Page from 'flow/components/Setting/Page';
import Loading from 'flow/components/ui-components/Loading';
import RouteLink from 'flow/components/ui-components/RouteLink';
import useAppContext from 'flow/hooks/useAppContext';
import useProfiles from 'flow/hooks/useProfiles';
import useSettingsNavigation from 'flow/hooks/useSettingsNavigation';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';
import { SettingsType } from 'flow/types/CallRecording';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import {
    getCallRecordingTitle,
    getCallRecordingValue,
} from 'flow/utils/call-recording-monitoring-utils';
import {
    callRecordingVaries,
    personalCallRecordingVaries,
} from 'flow/utils/profile-utils';
import { generatePath } from 'flow/utils/router-utils';

const CallRecordingAndMonitoring = () => {
    const { i18next } = useAppContext();
    const { isSinglePaneMode } = useSinglePaneMode();
    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();

    const { profiles, hasPermissionToProfile } = useProfiles(extensionKey);

    const { goToSettings } = useSettingsNavigation();

    if (!profiles?.size) {
        return <Loading signalFinishedLoading={true} />;
    }

    const getDescription = (type: SettingsType) => {
        if (type === SettingsType.Monitoring) {
            if (!hasPermissionToProfile('canBeCoMonitored')) {
                return i18next.t('settings.not_active');
            }

            const enabledProfiles = profiles.filter((profile) =>
                profile.get('canBeCoMonitored'),
            ).size;

            if (enabledProfiles === 0) {
                return i18next.t('settings.off');
            }

            if (enabledProfiles === profiles.size) {
                return i18next.t('settings.on');
            }

            return i18next.t('settings.number_of_profiles', {
                activeCount: enabledProfiles,
                profileCount: profiles.size,
            });
        }

        if (type === SettingsType.Personal) {
            if (personalCallRecordingVaries(profiles)) {
                return i18next.t('settings.varies_per_profile');
            }

            return i18next.t(
                `settings.call_recording.${getCallRecordingValue(profiles.first(), SettingsType.Personal, true)}`,
            );
        }

        if (callRecordingVaries(profiles)) {
            return i18next.t('settings.varies_per_profile');
        }

        return i18next.t(
            `settings.call_recording.${getCallRecordingValue(profiles.first(), SettingsType.Business, true)}`,
        );
    };

    return (
        <Page
            header={getCallRecordingTitle(profiles.first())}
            onClickBack={isSinglePaneMode ? goToSettings : undefined}
        >
            <RouteLink
                to={generatePath(
                    routes['settings.call-recording-and-monitoring.settings'],
                    { extensionKey, settingType: SettingsType.Business },
                )}
                className="py-4"
                description={getDescription(SettingsType.Business)}
                blocked={
                    !hasPermissionToProfile('recordCalls') &&
                    !hasPermissionToProfile('autoTranscribe')
                }
                blockedInformation={i18next.t(
                    'settings.call_recording.no_feature',
                )}
            >
                {i18next.t('settings.call_recording')}
            </RouteLink>

            {hasPermissionToProfile('personalRecordCalls') ||
            hasPermissionToProfile('personalAutoTranscribe') ? (
                <RouteLink
                    to={generatePath(
                        routes[
                            'settings.call-recording-and-monitoring.settings'
                        ],
                        { extensionKey, settingType: SettingsType.Personal },
                    )}
                    className="py-4"
                    description={getDescription(SettingsType.Personal)}
                >
                    {i18next.t('settings.call_recording_personal')}
                </RouteLink>
            ) : null}

            <RouteLink
                to={generatePath(
                    routes['settings.call-recording-and-monitoring.settings'],
                    { extensionKey, settingType: 'monitoring' },
                )}
                className="py-4"
                description={getDescription(SettingsType.Monitoring)}
                blocked={!hasPermissionToProfile('canBeCoMonitored')}
                blockedInformation={i18next.t(
                    'settings.call_recording.no_feature',
                )}
            >
                {i18next.t('settings.call_monitoring')}
            </RouteLink>
        </Page>
    );
};

export default CallRecordingAndMonitoring;
